import {
  Columns,
  Icon,
  Radio,
  Stack,
  Text,
  Tooltip,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import React, { useState, useEffect } from 'react'
import { ApiProduct } from '@vend/types'
import { trackCustomHeapEvent } from '@vend/utilities'
import {
  HEAP_LINE_ITEM_FULFILMENT_DECISION_ACTION,
  HEAP_PACKAGING_BREAKDOWN_OPTION_OBJECT,
} from '../../../sale-model.constant'
import { useAppSelector } from '../../../../../state/hooks'
import { selectCurrentSale } from '../../../../../state/slices/currentSale/currentSaleSlice'

import { ShouldFulfillOpt } from './LineItemFulfillmentContext'

export const TContext = 'Line item fulfilment popover'
export const TestId = 'lif-decision-view'

type Props = {
  decision: ShouldFulfillOpt
  productQuantity: number
  productName: string
  onSelect: (opt: ShouldFulfillOpt, isBreakdownSelected: boolean) => void
  canShowBreakdownOption?: boolean
  outerProduct?: ApiProduct
  saleHasServiceOrder?: boolean
}

// Internal enum for display purposes
enum DisplayOption {
  CONTINUE_ANYWAY = 'continue_anyway',
  BREAKDOWN = 'break_down',
  FULFIL_LATER = 'fulfil_later',
}

export const LineItemFulfilmentDecisionView: React.FC<Props> = ({
  decision,
  productQuantity,
  productName,
  onSelect,
  canShowBreakdownOption = false,
  outerProduct,
  saleHasServiceOrder = false,
}: Props) => {
  const t = useT()
  // LRX-38473: On layby sales the cashier must not be able to change the
  // fulfilment type when adding an out-of-stock product. The stock warning is
  // still shown so they can choose to keep ("Continue anyway") or remove the
  // item. The "Fulfill later" path that exposes Delivery/Pickup is kept in the
  // layout but disabled with a tooltip explaining why.
  const currentSale = useAppSelector(selectCurrentSale)
  const isLaybySale = !!currentSale.attributes?.includes('layby')

  const initialDisplayOption =
    decision === ShouldFulfillOpt.YES && !isLaybySale
      ? DisplayOption.FULFIL_LATER
      : DisplayOption.CONTINUE_ANYWAY

  const [
    selectedDisplayOption,
    setSelectedDisplayOption,
  ] = useState<DisplayOption>(initialDisplayOption)

  const handleOptionSelect = (displayOption: DisplayOption) => {
    setSelectedDisplayOption(displayOption)

    onSelect(
      displayOption === DisplayOption.FULFIL_LATER
        ? ShouldFulfillOpt.YES
        : ShouldFulfillOpt.NO,
      displayOption === DisplayOption.BREAKDOWN
    )
  }

  useEffect(() => {
    if (canShowBreakdownOption) {
      trackCustomHeapEvent(
        {
          action: HEAP_LINE_ITEM_FULFILMENT_DECISION_ACTION,
          object: HEAP_PACKAGING_BREAKDOWN_OPTION_OBJECT,
        },
        {
          productName,
          outerProductName: outerProduct?.name || '',
          productQuantity,
        }
      )
    }
    // intentionally omit dependencies to track this event only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack space={6} data-testid={TestId}>
      <Columns space={2}>
        <Text type="label">
          <Icon
            icon="fa-exclamation-triangle"
            className="wr-input-error-icon"
          />
        </Text>
        <Text type="label">
          <h3>
            {t(
              '{productQuantity, plural,' +
                'one {There’s {productQuantity} available of “{productName}”}' +
                'other {There’s {productQuantity} available of “{productName}”}}',
              { _context: TContext, productQuantity, productName }
            )}
          </h3>
        </Text>
      </Columns>

      <Stack space={2}>
        <Text type="label">
          <T _str="What do you want to do?" _context={TContext} />
        </Text>
        <Stack space={3}>
          <Radio
            className="vd-pl0"
            name="decision"
            label={t(
              saleHasServiceOrder
                ? "Add product anyway. It's available."
                : 'Continue anyway',
              { _context: TContext }
            )}
            value={DisplayOption.CONTINUE_ANYWAY}
            checked={selectedDisplayOption === DisplayOption.CONTINUE_ANYWAY}
            onChange={() => handleOptionSelect(DisplayOption.CONTINUE_ANYWAY)}
            data-track="continue-anyway-option"
          />
          {canShowBreakdownOption && (
            <Radio
              className="vd-pl0"
              name="decision"
              label={t('Break down from "{outerProductName}"', {
                _context: TContext,
                outerProductName:
                  outerProduct?.name ??
                  t('outer product', {
                    _context: 'outer product that is broken down',
                  }),
              })}
              value={DisplayOption.BREAKDOWN}
              checked={selectedDisplayOption === DisplayOption.BREAKDOWN}
              onChange={() => handleOptionSelect(DisplayOption.BREAKDOWN)}
              data-track="breakdown-option"
            />
          )}
          <Tooltip
            direction="below"
            canBeShown={isLaybySale}
            message={t(
              'Delivery and pickup aren’t available for layaway sales.',
              { _context: 'Tooltip text' }
            )}
          >
            <Radio
              className="vd-pl0"
              name="decision"
              label={t(
                saleHasServiceOrder
                  ? 'Request to order this product'
                  : 'Fulfill later',
                { _context: TContext }
              )}
              value={DisplayOption.FULFIL_LATER}
              checked={
                !isLaybySale &&
                selectedDisplayOption === DisplayOption.FULFIL_LATER
              }
              onChange={() =>
                !isLaybySale && handleOptionSelect(DisplayOption.FULFIL_LATER)
              }
              data-track="fulfil-later-option"
              disabled={isLaybySale}
            />
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  )
}
